// src/oura/ask/useAskData.ts
//
// Ask Oura (PREVIEW): the reads behind an answer. Every one is a call an
// existing screen already makes, so the chat and the screen it links to can
// never disagree:
//   Delivery           useDelivery            (Delivery.tsx)
//   Documents board    useBusinessDocuments   (ClientReviews.tsx)
//   The Answer Plan    useDocument            (the Client tab's doc drawer)
//   Change requests    useChangeRequests      (ClientReviews.tsx)
//   Visibility, scores, site, search, setup   useLiveFacts (liveFacts.ts: the Overview reads)
//   Today              getToday               (TodayList.tsx)
//   All clients docs   useDocsAcrossClients   (AllClientDocs.tsx)
// Reads only. Nothing here writes, sends or starts anything.
//
// A failed read is never empty data: every aggregate carries `error`, is
// `ready` only after its reads succeeded (Delivery's "no contract yet" is the
// one confirmed empty answer), and the answer builders say "Couldn't load"
// with a retry instead of stating facts from part of the records.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDelivery, getToday, subscribeDelivery, type DeliveryPayload, type TodayPayload } from "@service/aeo/delivery";
import type { BusinessDocuments, DocsAcrossClients, DocsFailure, DocumentDetail } from "@service/aeo/clientDocs";
import { getDocument, listBusinessDocuments } from "@service/aeo/clientDocs";
import { readChangeRequests, type ChangeRequest } from "@service/aeo/clientReview";
import { getBusiness } from "../data";
import { useDelivery } from "../delivery/useDelivery";
import { useBusinessDocuments, useChangeRequests, useDocsAcrossClients, useDocument } from "../docs/hooks";
import { namedRule, trendDelta, type EngineFilter } from "../visibility/model";
import { rivalFacts } from "../visibility/rivalFacts";
import { myDate, workingDaysBetween } from "@service/aeo/clientDocsRules";
import { agreedNotYetWork, answerPlanDocId, type AgreedSet } from "./agreed";
import { useLiveFacts, type AskFacts } from "./liveFacts";

export interface VisSummary {
    named: number;
    of: number;
    capturedAt: string;
    /** "A question counts once if ChatGPT or Gemini names the business. …": the screen's own rule. */
    rule: string;
    /** The band the Visibility tile shows ("Low"), or null. */
    verdict: string | null;
    engines: { label: string; named: number; of: number }[];
    /** Movement since the scan before, per series; null where it cannot be told. */
    moves: { label: string; value: number; since: string | null; comparable: boolean; now: number; of: number | null }[];
    /** Series measured for the first time on this scan. */
    firstTime: string[];
    /** The rivals named where the business is not, most first (the Competitors tab's order). */
    rivals: { name: string; takes: number; fresh: number; gap: string | null }[];
    /** The scan the Competitors tab compares with ("Since 18 Aug"), or null. */
    priorAt: string | null;
    /** Each engine on the scan before this one, by label, where the trend has it. */
    before: Record<string, { value: number; of: number | null; date: string | null } | null>;
}

/** The site audit's issues by severity, as the Site screen counts them. */
export interface IssueCounts {
    error: number;
    warning: number;
    notice: number;
    /** The worst issue, for a link that opens its How to fix drawer. */
    top: { key: string; label: string; severity: "error" | "warning" | "notice"; pages: number } | null;
}

export interface SnapshotSummary {
    total: number | null;
    previousTotal: number | null;
    capturedAt: string;
}

/**
 * A question we asked the client in a doc that is not itself waiting on
 * them (the doc's own wait is ours), still unanswered: the Client tab counts
 * these in "questions are late". Read from the doc's threads, as the doc
 * drawer reads them.
 */
export interface ClientQuestion {
    docId: string;
    docType: string;
    threadId: string;
    text: string;
    /** Working days since we last spoke on it (or since the doc was shared, if later). */
    days: number;
    late: boolean;
}

export interface ClientData {
    businessId: string;
    name: string;
    /** undefined while loading or after a failed read; null when the client has no Delivery yet. */
    delivery: DeliveryPayload | null | undefined;
    board: BusinessDocuments | null;
    requests: ChangeRequest[] | null;
    vis: VisSummary | null;
    snapshot: SnapshotSummary | null;
    agreed: AgreedSet | null;
    /** Open questions to the client inside docs whose own wait is ours; null until read. */
    clientQuestions: ClientQuestion[] | null;
    issues: IssueCounts | null;
    /** The client's own facts (scores, dates, questions, search, issues, setup), as the screens have them; null while reading. */
    facts: AskFacts | null;
    /** The first reads have landed and succeeded (Delivery, the board and the requests, and the Answer Plan once known). */
    ready: boolean;
    /** A required read failed: answer "Couldn't load", never from these fields. */
    error: boolean;
    /** Read the failed records again. */
    retry: () => void;
}

const SERIES: { key: EngineFilter; label: string }[] = [
    { key: "all", label: "All engines" },
    { key: "chatgpt", label: "ChatGPT" },
    { key: "gemini", label: "Gemini" },
    { key: "ai_overview", label: "Google's AI" },
];

const businessName = (id: string) => getBusiness(id)?.name ?? id;

function visSummary(businessId: string, facts: AskFacts | null): VisSummary | null {
    const inputs = facts?.visInputs ?? null;
    const m = facts?.vis ?? null;
    if (!inputs || !m) return null;
    if (m.headline.of <= 0) return null;
    const engines = Object.values(m.engines)
        .filter((e) => e.named !== null && e.of !== null && e.of > 0 && e.skipped === null)
        .map((e) => ({ label: e.label, named: e.named as number, of: e.of as number }));
    const last = m.trend.points[m.trend.points.length - 1];
    const prev = m.trend.points[m.trend.points.length - 2];
    const moves: VisSummary["moves"] = [];
    const firstTime: string[] = [];
    for (const s of SERIES) {
        const d = trendDelta(m.trend, s.key);
        if (d && last) moves.push({ label: s.label, value: d.value, since: d.since, comparable: d.comparable, now: last.values[s.key] ?? 0, of: last.of[s.key] });
        else if (last && prev && last.values[s.key] !== null && prev.values[s.key] === null) firstTime.push(s.label);
    }
    const before: VisSummary["before"] = {};
    for (const s of SERIES) {
        const v = prev?.values[s.key];
        before[s.label] = prev && v !== null && v !== undefined ? { value: v, of: prev.of[s.key], date: prev.date } : null;
    }
    const prior = inputs.prior ?? null;
    return {
        named: m.headline.named,
        of: m.headline.of,
        capturedAt: m.capturedAt,
        rule: namedRule(m.counted),
        verdict: m.headline.verdict,
        engines,
        moves,
        firstTime,
        // The same facts the Competitors tab prints: "Since 18 Aug: … 3 new questions taken".
        rivals: m.rivals
            .filter((r) => r.takes > 0)
            .map((r) => ({ name: r.name, takes: r.takes, fresh: rivalFacts(m, r, "all", facts?.name ?? businessName(businessId), prior).newWins.size, gap: r.gap })),
        priorAt: prior?.capturedAt ?? null,
        before,
    };
}

function issueCounts(facts: AskFacts | null): IssueCounts | null {
    const issues = facts?.issues;
    if (!issues) return null;
    const count = (s: "error" | "warning" | "notice") => issues.filter((i) => i.severity === s).length;
    const top = issues[0];
    return {
        error: count("error"),
        warning: count("warning"),
        notice: count("notice"),
        top: top ? { key: top.key, label: top.label, severity: top.severity, pages: top.pages } : null,
    };
}

/** The open client questions in docs whose own wait is ours (the board's count says which docs have any). */
function useClientQuestions(board: BusinessDocuments | null): { rows: ClientQuestion[] | null; failed: boolean; retry: () => void } {
    const docs = useMemo(
        // Only docs the client can see: a question in an unshared copy waits on nobody.
        () => (board?.documents ?? []).filter((d) => d.sharing.clientCanSee && !d.notClientDoc && d.waitingOn !== "client" && (d.questions?.waitingOnClient ?? 0) > 0).map((d) => d.id),
        [board],
    );
    // A retry is a new read: the result is kept under the docs it names and the attempt.
    const [attempt, setAttempt] = useState(0);
    const key = `${attempt}|${docs.join(",")}`;
    const [out, setOut] = useState<{ key: string; rows: ClientQuestion[]; failed: boolean } | null>(null);
    useEffect(() => {
        if (!board) return;
        let live = true;
        const today = myDate(new Date());
        void Promise.all(docs.map((id) => getDocument(id))).then((reads) => {
            if (!live) return;
            const rows: ClientQuestion[] = [];
            let failed = false;
            for (const r of reads) {
                // A doc that could not be read is a failure, not a doc with no questions.
                if (!r.ok || !r.data) {
                    failed = true;
                    continue;
                }
                const doc = r.data;
                for (const t of doc.threads) {
                    if (t.kind !== "question" || (t.state !== "waiting_on_client" && t.state !== "late")) continue;
                    const lastAt = t.posts.length ? t.posts[t.posts.length - 1].at : t.openedAt;
                    const since = [lastAt, doc.sharing.sharedAt].filter((x): x is string => !!x).sort().pop() ?? lastAt;
                    rows.push({
                        docId: doc.id,
                        docType: doc.docType ?? doc.name,
                        threadId: t.id,
                        text: t.text.replace(/^\s*\[[A-Z0-9-]+\]\s*/, "").split("\n")[0],
                        days: workingDaysBetween(myDate(since), today),
                        late: t.state === "late",
                    });
                }
            }
            setOut({ key, rows: failed ? [] : rows, failed });
        });
        return () => {
            live = false;
        };
        // `key` names the docs and the attempt; `docs` is rebuilt with it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, board]);
    const retry = useCallback(() => setAttempt((a) => a + 1), []);
    const mine = board && out && out.key === key ? out : null;
    return { rows: mine && !mine.failed ? mine.rows : null, failed: !!mine?.failed, retry };
}

/** The first read failed with nothing to show (a failed refresh after a good read keeps that read). */
const firstReadFailed = (l: { status: string; error: DocsFailure | null }): DocsFailure | null => (l.status === "error" ? l.error : null);

/** The longest a retry holds its failures back: it ends sooner, as each read it re-ran comes back. */
const RETRY_HOLD_MS = 10_000;

/**
 * A retry's hold on the failures it is reading again, so Try again is not
 * answered "Couldn't load" from the failure it is retrying. The hold ends
 * once, for good, when every re-run read has come back (any new result,
 * good or bad) or RETRY_HOLD_MS has passed, whichever is first. The time
 * bound means a read that fails again with the very same failure value, or
 * never answers, still ends it: a retry is never left pending.
 */
function useRetryHold(failures: readonly (DocsFailure | null)[]): { pending: boolean; hold: () => void } {
    const [held, setHeld] = useState<{ id: number; failures: readonly (DocsFailure | null)[] } | null>(null);
    const latest = useRef(failures);
    latest.current = failures;
    const ids = useRef(0);
    const hold = useCallback(() => {
        const now = latest.current;
        // Nothing to hold (a read with no failure value, Delivery or the questions, shows its own loading).
        setHeld(now.some((f) => f !== null) ? { id: ++ids.current, failures: now } : null);
    }, []);
    const pending = !!held && held.failures.some((f, i) => f !== null && f === failures[i]);
    useEffect(() => {
        if (!held) return;
        // Released: drop the hold, so a later failure is never held back by an old retry.
        if (!pending) {
            setHeld(null);
            return;
        }
        const id = held.id;
        const timer = window.setTimeout(() => setHeld((h) => (h?.id === id ? null : h)), RETRY_HOLD_MS);
        return () => window.clearTimeout(timer);
    }, [held, pending]);
    return { pending, hold };
}

export function useClientData(businessId: string): ClientData {
    const business = getBusiness(businessId);
    const { load, reload: reloadDelivery } = useDelivery(businessId, business?.name);
    const board = useBusinessDocuments(businessId);
    const requests = useChangeRequests(businessId);
    // Only a confirmed "no contract yet" is null; a failed read stays unknown.
    const delivery = load.state === "ready" ? load.data : load.state === "none" ? null : undefined;
    const planId = answerPlanDocId(board.data);
    const plan = useDocument(planId);
    const live = useLiveFacts(businessId);
    const facts = live.facts;
    const issues = useMemo(() => issueCounts(facts), [facts]);
    const vis = useMemo(() => visSummary(businessId, facts), [businessId, facts]);
    const snap = useMemo(() => {
        if (!facts) return null;
        const sc = facts.scores;
        // The same blend Overview's headline uses: the two scores' mean, only when both exist.
        const blend = (a: number | null, b: number | null) => (typeof a === "number" && typeof b === "number" ? Math.round((a + b) / 2) : null);
        return { total: blend(sc?.aeo ?? null, sc?.siteHealth ?? null), previousTotal: sc?.prev ? blend(sc.prev.aeo, sc.prev.siteHealth) : null, capturedAt: facts.lastScanAt ?? "" };
    }, [facts]);
    const agreed = useMemo(() => (delivery === undefined ? null : agreedNotYetWork(plan.data, delivery)), [plan.data, delivery]);
    const questions = useClientQuestions(board.data);
    const clientQuestions = questions.rows;

    const boardFailed = firstReadFailed(board);
    const requestsFailed = firstReadFailed(requests);
    const planFailed = planId === null ? null : firstReadFailed(plan);
    // A retry holds the failures it is reading again until each one comes back (a new result, good or bad), bounded in time.
    const { pending, hold } = useRetryHold([boardFailed, requestsFailed, planFailed]);
    const error = !pending && (load.state === "failed" || !!boardFailed || !!requestsFailed || !!planFailed || questions.failed || live.error);
    const ready =
        !error &&
        !pending &&
        delivery !== undefined &&
        board.status === "ready" &&
        requests.status === "ready" &&
        (planId === null || plan.status === "ready") &&
        (!board.data || clientQuestions !== null) &&
        !live.loading;
    const retry = useCallback(() => {
        hold();
        if (live.error) live.retry();
        if (load.state === "failed") void reloadDelivery(false);
        if (boardFailed) board.reload();
        if (requestsFailed) requests.reload();
        if (planFailed) plan.reload();
        if (questions.failed) questions.retry();
    }, [hold, boardFailed, requestsFailed, planFailed, load.state, reloadDelivery, board, requests, plan, questions, live]);
    return {
        businessId,
        name: facts?.name ?? business?.name ?? (delivery ? delivery.businessName : businessId),
        delivery,
        board: board.data,
        requests: requests.data,
        vis,
        snapshot: snap,
        agreed,
        clientQuestions,
        issues,
        facts,
        ready,
        error,
        retry,
    };
}

/** Only the approval suggestion, for the card on Delivery. */
export function useAgreedAnswers(businessId: string, delivery: DeliveryPayload | null): AgreedSet | null {
    const board = useBusinessDocuments(businessId);
    const plan = useDocument(answerPlanDocId(board.data));
    return useMemo(() => agreedNotYetWork(plan.data, delivery), [plan.data, delivery]);
}

/**
 * The approved answers for the Page workshop (PREVIEW): read only where
 * `enabled` (the sample business), so a live client's workshop makes no
 * extra reads. The same rule as the Delivery card: agreed, and no draft
 * started from it yet. The Delivery read is kept under the business it was
 * made for, so a switch never pairs one client's Delivery with another's
 * Answer Plan; a failed read shows no suggestion rather than treating every
 * approved answer as not yet drafted.
 */
export function usePlanAnswers(businessId: string, enabled: boolean): AgreedSet | null {
    const [deliveryRead, setDeliveryRead] = useState<{ key: string; data: DeliveryPayload | null; failed: boolean } | null>(null);
    useEffect(() => {
        if (!enabled) return;
        let live = true;
        void getDelivery(businessId).then((r) => {
            if (live) setDeliveryRead({ key: businessId, data: r.ok ? r.data : null, failed: !r.ok });
        });
        return () => {
            live = false;
        };
    }, [businessId, enabled]);
    const current = deliveryRead?.key === businessId ? deliveryRead : null;
    const board = useBusinessDocuments(enabled ? businessId : null);
    const plan = useDocument(enabled ? answerPlanDocId(board.data) : null);
    return useMemo(
        () => (enabled && current && !current.failed && plan.status === "ready" ? agreedNotYetWork(plan.data, current.data) : null),
        [enabled, current, plan.status, plan.data],
    );
}

export interface AllData {
    today: TodayPayload | null;
    /** Each client's Delivery (by business id), the read Delivery.tsx makes; absent where a client has none. */
    delivery: Record<string, DeliveryPayload>;
    across: DocsAcrossClients | null;
    /** Each client's change requests, the Client tab's Requests read (by business id). */
    requests: Record<string, ChangeRequest[]>;
    /** Each client's approved answers no draft has started from (agreed.ts), where there are any. */
    agreed: Record<string, AgreedSet>;
    /** Every read succeeded. */
    ready: boolean;
    /** A read failed (Today, the documents summary, or any client's): answer "Couldn't load", never from these maps. */
    error: boolean;
    /** Read the failed records again. */
    retry: () => void;
}

interface PerClient {
    key: string;
    delivery: Record<string, DeliveryPayload>;
    requests: Record<string, ChangeRequest[]>;
    agreed: Record<string, AgreedSet>;
    /** The clients whose reads failed; any one makes the whole set an error. */
    failed: string[];
}

/* ------------------------------------------------------------------------- *
 * All clients: bounded, cached per client
 *
 * There is no batch read yet, so each client costs its own requests, board,
 * Delivery and Answer Plan reads. Three limits keep that from flooding the
 * browser or the API:
 *   - at most MAX_READS of these reads are in flight at once, across clients;
 *   - a client's good result is kept (by business id) for FRESH_MS and reused,
 *     so opening another client's chat does not read every client again;
 *   - a Delivery change (the broadcast names no business) is coalesced and
 *     re-reads only Delivery, reusing each client's cached requests and plan.
 * Follow-up: one batch backend endpoint, getAskOuraOverview(ids), returning
 * Delivery, requests and the approved-answer inputs for many clients in one
 * call. It does not exist yet; this module should switch to it once it does.
 * ------------------------------------------------------------------------- */

const MAX_READS = 4;
const FRESH_MS = 60_000;
const COALESCE_MS = 300;

/** In-flight counts, for the dev check that the cap holds (window.__askOuraReads, dev builds only). */
const readStats = { inFlight: 0, peak: 0, total: 0 };
if (import.meta.env.DEV && typeof window !== "undefined") (window as unknown as { __askOuraReads?: typeof readStats }).__askOuraReads = readStats;

const waiting: (() => void)[] = [];

/** Run one read when a slot is free. */
function limited<T>(run: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const start = () => {
            readStats.inFlight += 1;
            readStats.total += 1;
            readStats.peak = Math.max(readStats.peak, readStats.inFlight);
            run()
                .then(resolve, reject)
                .finally(() => {
                    readStats.inFlight -= 1;
                    waiting.shift()?.();
                });
        };
        if (readStats.inFlight < MAX_READS) start();
        else waiting.push(start);
    });
}

interface ClientRead {
    at: number;
    /** The Delivery generation this read's Delivery is current for. */
    deliveryGeneration: number;
    delivery: DeliveryPayload | null;
    requests: ChangeRequest[];
    plan: DocumentDetail | null;
}

/** Good reads only: a failure is never cached, so the next read (or a retry) asks again. */
const clientCache = new Map<string, ClientRead>();
const clientInFlight = new Map<string, Promise<ClientRead | null>>();

/**
 * Bumped by every Delivery change. A read records the generation it started
 * at, so a change that lands while it runs (even a client's first read, not
 * cached yet) is never cached as current.
 */
let deliveryGeneration = 0;
/** Delivery re-reads one read makes when changes keep landing; after that the read fails (Couldn't load, with a retry). */
const MAX_DELIVERY_REREADS = 2;

function markDeliveryStale(): void {
    deliveryGeneration += 1;
}

/** One client's Delivery, read again while a change landed during the read (bounded). */
async function readCurrentDelivery(id: string): Promise<{ data: DeliveryPayload | null; generation: number } | null> {
    for (let attempt = 0; ; attempt += 1) {
        const generation = deliveryGeneration;
        const r = await limited(() => getDelivery(id));
        if (!r.ok) return null;
        if (generation === deliveryGeneration) return { data: r.data, generation };
        // Still overtaken after the bounded re-reads: a failed read, never a stale fact the answer states.
        if (attempt >= MAX_DELIVERY_REREADS) return null;
    }
}

const isFresh = (c: ClientRead | undefined): c is ClientRead => !!c && Date.now() - c.at < FRESH_MS;

/** One client's reads (requests, board, Delivery, then the Answer Plan), or null when any failed. */
async function readOneClient(id: string, joined = 0): Promise<ClientRead | null> {
    const cached = clientCache.get(id);
    if (isFresh(cached) && cached.deliveryGeneration === deliveryGeneration) return cached;
    const going = clientInFlight.get(id);
    if (going) {
        const r = await going;
        // A read already out may have started before the latest change: read again once it is done.
        if (r && r.deliveryGeneration !== deliveryGeneration) {
            // Still overtaken after the bounded re-reads: fail the client, never answer from the stale read.
            if (joined < MAX_DELIVERY_REREADS) return readOneClient(id, joined + 1);
            return null;
        }
        return r;
    }
    const run = (async (): Promise<ClientRead | null> => {
        if (isFresh(cached)) {
            // Only Delivery changed: keep the requests and the plan this client already read.
            const latest = await readCurrentDelivery(id);
            if (!latest) return null;
            const next: ClientRead = { ...cached, delivery: latest.data, deliveryGeneration: latest.generation };
            clientCache.set(id, next);
            return next;
        }
        const [req, board, latest] = await Promise.all([limited(() => readChangeRequests(id)), limited(() => listBusinessDocuments(id)), readCurrentDelivery(id)]);
        // Delivery's ok-and-null is the confirmed "no contract yet"; every other failure fails the client.
        if (!req.ok || !board.ok || !latest) return null;
        const planId = answerPlanDocId(board.data);
        let plan: DocumentDetail | null = null;
        if (planId) {
            const doc = await limited(() => getDocument(planId));
            if (!doc.ok) return null;
            plan = doc.data;
        }
        const next: ClientRead = { at: Date.now(), deliveryGeneration: latest.generation, delivery: latest.data, requests: req.data, plan };
        clientCache.set(id, next);
        return next;
    })().finally(() => clientInFlight.delete(id));
    clientInFlight.set(id, run);
    return run;
}

/**
 * The reads the Client tab and Delivery make, once per client the documents
 * summary lists: its requests, and its Answer Plan's approved answers. Reads
 * only, the same service calls those screens' hooks wrap, through the cap
 * and the cache above.
 */
async function readPerClient(ids: string[]): Promise<Omit<PerClient, "key">> {
    const deliveries: Record<string, DeliveryPayload> = {};
    const requests: Record<string, ChangeRequest[]> = {};
    const agreed: Record<string, AgreedSet> = {};
    const failed: string[] = [];
    await Promise.all(
        ids.map(async (id) => {
            const r = await readOneClient(id).catch(() => null);
            if (!r) {
                failed.push(id);
                return;
            }
            requests[id] = r.requests;
            if (r.delivery) deliveries[id] = r.delivery;
            const set = agreedNotYetWork(r.plan, r.delivery);
            if (set) agreed[id] = set;
        }),
    );
    return { delivery: deliveries, requests, agreed, failed };
}

/**
 * Dev fixtures named "[Mock] …" are test rows, never a client. Ask Oura
 * leaves them out of every answer, so no count or row can include one.
 */
export const isMockName = (name: string | null | undefined): boolean => /^\s*\[mock\]/i.test(name ?? "");

/** The documents read across clients, without the mock fixtures. */
function withoutMocks(across: DocsAcrossClients | null): DocsAcrossClients | null {
    if (!across) return null;
    const mock = new Set(across.clients.filter((c) => isMockName(c.name)).map((c) => c.businessId));
    const real = (r: { businessId: string; businessName?: string }) => !mock.has(r.businessId) && !isMockName(r.businessName);
    return {
        ...across,
        clients: across.clients.filter((c) => !mock.has(c.businessId)),
        waitingOnClient: across.waitingOnClient.filter(real),
        needsUs: across.needsUs.filter(real),
        late: across.late.filter(real),
    };
}

/** Today's rows, without the mock fixtures. */
function todayWithoutMocks(today: TodayPayload | null): TodayPayload | null {
    if (!today) return null;
    return {
        ...today,
        rows: today.rows.filter((r) => !isMockName(r.businessName)),
        notSet: today.notSet.filter((r) => !isMockName(r.businessName)),
    };
}

/** The same read and refresh TodayList makes. */
export function useAllData(): AllData {
    // A failed refresh keeps the last good read; a failed first read is an error.
    const [today, setToday] = useState<{ data: TodayPayload; failed: false } | { data: null; failed: true } | undefined>(undefined);
    const seq = useRef(0);
    const readToday = useCallback(() => {
        const mine = ++seq.current;
        void getToday().then((r) => {
            if (mine !== seq.current) return;
            setToday((prev) => (r.ok ? { data: r.data, failed: false } : prev && !prev.failed ? prev : { data: null, failed: true }));
        });
    }, []);
    useEffect(() => {
        readToday();
        const off = subscribeDelivery(readToday);
        return () => {
            // A read still out lands on nothing.
            seq.current += 1;
            off();
        };
    }, [readToday]);
    const acrossRead = useDocsAcrossClients();
    const across = useMemo(() => ({ status: acrossRead.status, data: withoutMocks(acrossRead.data) }), [acrossRead.status, acrossRead.data]);
    const ids = (across.data?.clients ?? []).map((c) => c.businessId);
    const idsKey = ids.join(",");
    const [per, setPer] = useState<PerClient | null>(null);
    // A Delivery change re-reads, as Today does, so the late rows follow it:
    // coalesced, and Delivery only (the cache keeps each client's requests and plan).
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let timer: number | undefined;
        const off = subscribeDelivery(() => {
            markDeliveryStale();
            window.clearTimeout(timer);
            timer = window.setTimeout(() => setTick((t) => t + 1), COALESCE_MS);
        });
        return () => {
            off();
            window.clearTimeout(timer);
        };
    }, []);
    useEffect(() => {
        if (!idsKey) {
            setPer(null);
            return;
        }
        let live = true;
        void readPerClient(idsKey.split(",")).then((r) => {
            if (live) setPer({ key: idsKey, ...r });
        });
        return () => {
            live = false;
        };
    }, [idsKey, tick]);
    // Only the maps read for this exact set of clients.
    const currentPer = idsKey && per?.key === idsKey ? per : null;
    const acrossFailed = firstReadFailed(acrossRead);
    const { pending: acrossPending, hold: holdAcross } = useRetryHold([acrossFailed]);
    const error = !acrossPending && (!!today?.failed || !!acrossFailed || (currentPer?.failed.length ?? 0) > 0);
    const perReady = across.status === "ready" && (!idsKey || currentPer !== null);
    const todayReal = useMemo(() => todayWithoutMocks(today?.data ?? null), [today]);
    const retry = useCallback(() => {
        if (today?.failed) {
            setToday(undefined);
            readToday();
        }
        if (acrossFailed) {
            holdAcross();
            acrossRead.reload();
        }
        if (per?.failed.length) {
            // Failed clients were not cached, so this reads only them again.
            setPer(null);
            setTick((t) => t + 1);
        }
    }, [today, readToday, acrossFailed, holdAcross, acrossRead, per]);
    return {
        today: todayReal,
        delivery: currentPer?.delivery ?? {},
        across: across.data,
        requests: currentPer?.requests ?? {},
        agreed: currentPer?.agreed ?? {},
        ready: !error && !acrossPending && today !== undefined && perReady,
        error,
        retry,
    };
}
